import { useState } from 'react';
import { Badge, Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger, Skeleton } from 'lift-ui';
import { jobsErrorMessage } from '../../api';
import { useSyncUsers } from '../../hooks';
import type { SyncUser, SyncUserCounts, SyncUserType } from '../../types';
import { formatCount } from '../../utils';
import { TYPES, TYPE_LABEL, TYPE_SINGULAR, userKey, useDebouncedValue, type Selection } from './shared';

const PAGE_SIZE = 25;

interface PeoplePickerProps {
  selection: Selection;
  counts: SyncUserCounts | undefined;
  disabled?: boolean;
  onAdd: (users: SyncUser[]) => void;
  onRemove: (users: SyncUser[]) => void;
  onClear: () => void;
}

export const PeoplePicker = ({ selection, counts, disabled, onAdd, onRemove, onClear }: PeoplePickerProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<SyncUserType | undefined>(undefined);
  const [page, setPage] = useState(1);
  const q = useDebouncedValue(search.trim(), 300);

  const users = useSyncUsers({ type, q: q || undefined, page, pageSize: PAGE_SIZE }, open);
  const rows = users.data?.data ?? [];
  const totalPages = users.data?.pagination.total_pages ?? 1;
  const selectedCount = Object.keys(selection).length;
  const allShownSelected = rows.length > 0 && rows.every((user) => selection[userKey(user)]);

  const toggle = (user: SyncUser, checked: boolean) => (checked ? onAdd([user]) : onRemove([user]));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} aria-haspopup="dialog">
          Choose people · <span className="ml-1 tabular-nums">{formatCount(selectedCount)} selected</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex w-[min(30rem,calc(100vw-2rem))] flex-col gap-3 p-3"
        aria-label="Choose people to sync"
      >
        <Input
          searchBar
          placeholder="Search by name or email"
          aria-label="Search people"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />

        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by type">
          <Button
            type="button"
            size="sm"
            variant={type === undefined ? 'default' : 'ghost'}
            aria-pressed={type === undefined}
            onClick={() => {
              setType(undefined);
              setPage(1);
            }}
          >
            All types
          </Button>
          {TYPES.map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={type === value ? 'default' : 'ghost'}
              aria-pressed={type === value}
              onClick={() => {
                setType(value);
                setPage(1);
              }}
            >
              {TYPE_LABEL[value]}
              {counts && <span className="ml-1.5 tabular-nums opacity-70">{formatCount(counts[value])}</span>}
            </Button>
          ))}
        </div>

        <div
          className="flex max-h-72 min-h-24 flex-col overflow-y-auto rounded-md border border-border"
          role="group"
          aria-label="People"
          aria-busy={users.isFetching}
        >
          {users.isLoading && (
            <div className="flex flex-col gap-2 p-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-8 w-full" />
              ))}
            </div>
          )}

          {users.isError && (
            <p className="p-3 text-sm text-destructive">
              {jobsErrorMessage(users.error, 'People could not be loaded.')}
            </p>
          )}

          {!users.isLoading && !users.isError && rows.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">
              {q ? `No one matches "${q}".` : 'No eligible people found.'}
            </p>
          )}

          {rows.map((user) => {
            const checked = Boolean(selection[userKey(user)]);
            const id = `sync-user-${userKey(user)}`;
            return (
              <label
                key={userKey(user)}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-3 border-b border-border px-3 py-2 last:border-b-0 hover:bg-muted/40"
              >
                <Checkbox
                  id={id}
                  checked={checked}
                  aria-label={`Select ${user.email}`}
                  onCheckedChange={(next) => toggle(user, next === true)}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm">{user.email}</span>
                  {user.name && <span className="truncate text-xs text-muted-foreground">{user.name}</span>}
                </span>
                <Badge variant="greyLight">{TYPE_SINGULAR[user.type]}</Badge>
              </label>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={rows.length === 0}
              onClick={() => (allShownSelected ? onRemove(rows) : onAdd(rows))}
            >
              {allShownSelected ? 'Unselect shown' : 'Select shown'}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={selectedCount === 0} onClick={onClear}>
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Button type="button" size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className="tabular-nums">
              Page {page} of {totalPages || 1}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={users.data?.pagination.last_page !== false}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
